import React from "react";
import "./RegistroIngreso.css";
import SectionHeader from "../../../components/common/PL/Header/SectionHeader";
import EntryRegistrationForm from "./components/EntryRegistrationForm";
import { useLocation, useHistory } from "react-router-dom";
import { RoutePaths } from "../../../utils/Items";
import {
  isRegistrationMode,
  isRegistroIngresoNavigationIntent,
  RegistroIngresoNavigationState,
} from "./registroIngresoNavigation";
import { useRegistroIngresoSession } from "./session/RegistroIngresoSessionContext";

const RegistroIngreso = () => {
  const location = useLocation<RegistroIngresoNavigationState | undefined>();
  const {
    tabs,
    activeTabId,
    startSession,
    appendTab,
    updateTabValues,
    searchUser,
    setActiveTab,
    findExistingTab,
  } = useRegistroIngresoSession();

  const processedLocationKeyRef = React.useRef<string | undefined>();
  const safeLocationState = location.state ?? {};
  const navigationMode = isRegistrationMode(safeLocationState.mode)
    ? safeLocationState.mode
    : undefined;
  const navigationIntent = isRegistroIngresoNavigationIntent(safeLocationState.intent)
    ? safeLocationState.intent
    : undefined;
  const navigationPrefill = safeLocationState.prefill;

  const history = useHistory();

  React.useEffect(() => {
    const isRefreshOrDirectAccess = history.action === "POP" && tabs.length === 0;

    if (isRefreshOrDirectAccess || (tabs.length === 0 && !navigationPrefill?.documentNumber)) {
      history.replace(RoutePaths.LogisticParkHome);
      return;
    }

    if (processedLocationKeyRef.current === location.key) return;
    processedLocationKeyRef.current = location.key;

    if (navigationIntent === "replace-session") {
      const existingTab =
        navigationPrefill?.documentNumber && navigationPrefill?.documentCountry
          ? findExistingTab(
              navigationPrefill.documentNumber,
              navigationPrefill.documentCountry,
              navigationMode ?? "entry"
            )
          : undefined;

      if (existingTab) {
        setActiveTab(existingTab.id);
      } else {
        const tabId = startSession(navigationMode ?? "entry", navigationPrefill);
        if (
          navigationMode === "exit" &&
          navigationPrefill?.documentNumber &&
          navigationPrefill?.documentCountry
        ) {
          searchUser(
            tabId,
            navigationPrefill.documentNumber,
            navigationPrefill.documentCountry,
            navigationMode,
            navigationPrefill.entranceEvent,
            {
              passId: navigationPrefill.passId,
              passType: navigationPrefill.passType,
              passGuestsType: navigationPrefill.passGuestsType,
              isBuildingMismatch: navigationPrefill.isBuildingMismatch,
            },
            navigationPrefill.id
          );
        }
      }
      return;
    }
    if (navigationIntent === "append-tab") {
      const existingTab =
        navigationPrefill?.documentNumber && navigationPrefill?.documentCountry
          ? findExistingTab(
              navigationPrefill.documentNumber,
              navigationPrefill.documentCountry,
              navigationMode ?? "entry"
            )
          : undefined;

      if (existingTab) {
        setActiveTab(existingTab.id);
      } else {
        const tabId = appendTab(navigationPrefill, navigationMode);
        if (
          navigationMode === "exit" &&
          navigationPrefill?.documentNumber &&
          navigationPrefill?.documentCountry
        ) {
          searchUser(
            tabId,
            navigationPrefill.documentNumber,
            navigationPrefill.documentCountry,
            navigationMode,
            navigationPrefill.entranceEvent,
            {
              passId: navigationPrefill.passId,
              passType: navigationPrefill.passType,
              passGuestsType: navigationPrefill.passGuestsType,
              isBuildingMismatch: navigationPrefill.isBuildingMismatch,
            },
            navigationPrefill.id
          );
        }
      }
      return;
    }
    if (navigationIntent === "keep-session") {
      if (tabs.length === 0) {
        startSession(navigationMode ?? "entry");
      }
      return;
    }
    if (tabs.length === 0) {
      const existingTab =
        navigationPrefill?.documentNumber && navigationPrefill?.documentCountry
          ? findExistingTab(
              navigationPrefill.documentNumber,
              navigationPrefill.documentCountry,
              navigationMode ?? "entry"
            )
          : undefined;

      if (existingTab) {
        setActiveTab(existingTab.id);
      } else {
        const tabId = startSession(navigationMode ?? "entry", navigationPrefill);
        if (
          navigationMode === "exit" &&
          navigationPrefill?.documentNumber &&
          navigationPrefill?.documentCountry
        ) {
          searchUser(
            tabId,
            navigationPrefill.documentNumber,
            navigationPrefill.documentCountry,
            navigationMode,
            navigationPrefill.entranceEvent,
            {
              passId: navigationPrefill.passId,
              passType: navigationPrefill.passType,
              passGuestsType: navigationPrefill.passGuestsType,
              isBuildingMismatch: navigationPrefill.isBuildingMismatch,
            },
            navigationPrefill.id
          );
        }
      }
    }
  }, [
    location.key,
    navigationIntent,
    navigationMode,
    navigationPrefill,
    startSession,
    appendTab,
    searchUser,
    tabs.length,
  ]);

  const activeTab = tabs.find((t) => t.id === activeTabId);
  const headerTitle = activeTab?.mode === "exit" ? "Registro de egreso" : "Registro de ingreso";

  return (
    <div className="registroIngresoContainer">
      <div className="registroIngresoHeaderSection">
        <SectionHeader title={headerTitle} />
      </div>
      <div className="registroIngresoBody">
        {tabs.map((tab) => (
          <div key={tab.id} style={{ display: tab.id === activeTabId ? "block" : "none" }}>
            <EntryRegistrationForm
              tabId={tab.id}
              mode={tab.mode}
              values={tab.values}
              onValuesChange={updateTabValues}
              vehicles={tab.vehicles}
              authorizations={tab.authorizations}
              isSearching={tab.isSearching}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RegistroIngreso;
